import DOMPurify from 'isomorphic-dompurify';
import json2md from 'json2md';
import { marked, Renderer } from 'marked';
import { TrustedHTML } from 'trusted-types/lib';
import TurndownService from 'turndown';

import { TableColors } from '../constants/custom-report-theme.constants';
import { ICodeHighlighter, ICodeHighlighterResponse } from '../interfaces/code-highlighter.interface';
import { SharedCommonUtility } from './common.utility';
import { SharedTextUtility } from './text.utility';

/**
 * Input type for json2md table converter.
 * @see https://github.com/IonicaBizau/json2md#available-converters
 */
export interface Json2MdInput {
  h5?: string;
  table?: {
    headers: string[];
    rows: string[][] | Record<string, string>[];
  };
}

enum TokenType {
  HEADING = 'heading',
  HR = 'hr',
  LINK = 'link',
  LIST = 'list',
  LIST_ITEM = 'listItem',
  TABLE = 'table',
  TEXT = 'text',
}

export enum RenderType {
  DEFAULT = 'DEFAULT',
  NOTIFICATION = 'NOTIFICATION',
  AICHAT = 'AICHAT',
  REPORTING_AGENT = 'REPORTING_AGENT',
}

const RenderTypeSanitizeConfig: Record<RenderType, any> = {
  DEFAULT: {},
  NOTIFICATION: {},
  AICHAT: {
    ADD_ATTR: ['target', 'class'],
    ADD_TAGS: ['pre', 'code'],
  },
  REPORTING_AGENT: {},
};

type ITokenClasses = Partial<Record<TokenType, string>>;

const RenderTypeLinkClasses: Record<RenderType, ITokenClasses> = {
  DEFAULT: {},
  NOTIFICATION: {},
  AICHAT: {
    [TokenType.LINK]: 'ds-link inline',
    [TokenType.LIST_ITEM]: 'ds-link inline',
  },
  REPORTING_AGENT: {},
};

interface Token {
  type: TokenType;
  raw: string;
  text?: string;
}

interface MarkedOptions {
  renderer: Renderer;
  walkTokens?: ((token: Token) => void) | undefined;
  breaks?: boolean | undefined;
  gfm?: boolean | undefined;
}

export class MarkdownUtility {
  private static readonly markedOptions: Map<RenderType, MarkedOptions> = new Map();
  private static readonly tokenTypesToChangeForNotification: Set<string> = new Set([
    TokenType.HEADING,
    TokenType.HR,
    TokenType.LINK,
    TokenType.LIST,
    TokenType.TABLE,
  ]);

  private static readonly turndownService: TurndownService = new TurndownService({
    headingStyle: 'atx',
  });

  private static codeHighlighter: ICodeHighlighter | null = null;

  /**
   * Special symbols combination that is replaced with <br> tag by the marked library
   */
  public static readonly br: string = '  \n';

  private static readonly BLANK_LINE_PLACEHOLDER: string = '\u200B\u200C\u200D'; // Zero-width characters

  // Shared pieces of the fenced-code-block pattern, so every consumer matches the same fences.
  // The info string allows mixed case and symbols (e.g. `TypeScript`, `C#`, `c++`).
  private static readonly FENCE_OPEN_SOURCE: string = '```[a-zA-Z0-9+#-]*\\n';
  private static readonly FENCE_CLOSE_SOURCE: string = '\\n```';

  /**
   * Sets the code highlighter to be used, allowing for client-side code to provide syntax highlighting functionality.
   */
  public static setCodeHighlighter(highlighter: ICodeHighlighter | null): void {
    this.codeHighlighter = highlighter;
  }

  private static getMarkedOptions(type: RenderType): any {
    if (!this.markedOptions.has(type)) {
      this.markedOptions.set(type, this.buildMarkedOptions(type));
    }

    return this.markedOptions.get(type);
  }

  private static buildMarkedOptions(type: RenderType): any {
    switch (type) {
      case RenderType.NOTIFICATION:
        return this.buildMarkedOptionsForNotification();
      case RenderType.AICHAT:
        return this.buildMarkedOptionsForAIChat();
      case RenderType.REPORTING_AGENT:
        return this.buildMarkedOptionsForReportingAgent();
      default:
        return this.buildDefaultMarkedOptions();
    }
  }

  private static buildMarkedOptionsForNotification(): any {
    const renderer: Renderer = new Renderer();

    renderer.code = function (text: string): string {
      return `<code>${text}</code>`;
    };

    renderer.codespan = function (text: string): string {
      return `<code>${SharedTextUtility.unescapeAmpersands(text)}</code>`;
    };

    renderer.paragraph = renderer.text;

    return {
      renderer: renderer,
      walkTokens: this.changeTokenTypeToText,
    };
  }

  private static changeTokenTypeToText = (token: Token): void => {
    if (MarkdownUtility.tokenTypesToChangeForNotification.has(token.type)) {
      token.type = TokenType.TEXT;

      if (SharedCommonUtility.isNullish(token['text'])) {
        token['text'] = token.raw;
      }
    }
  };

  private static buildMarkedOptionsForAIChat(): MarkedOptions {
    const renderer: Renderer = new Renderer();
    const tokenClasses: ITokenClasses = RenderTypeLinkClasses[RenderType.AICHAT];

    renderer.code = function (text: string, language: string): string {
      const restoredText: string = text.replace(new RegExp(MarkdownUtility.BLANK_LINE_PLACEHOLDER, 'g'), '');

      if (MarkdownUtility.codeHighlighter && language) {
        const unescapedText: string = SharedTextUtility.unescapeHtmlTags(restoredText);
        const codeHighlighterResponse: ICodeHighlighterResponse = MarkdownUtility.codeHighlighter.highlight(
          unescapedText,
          language,
        );
        return `<pre><code class="hljs language-${codeHighlighterResponse.language}">${codeHighlighterResponse.code}</code></pre>`;
      }

      const escapedText: string = SharedTextUtility.escapeHtmlTags(restoredText);
      return `<pre><code>${escapedText}</code></pre>`;
    };

    renderer.codespan = function (text: string): string {
      const escapedText: string = SharedTextUtility.unescapeAmpersands(text.trim());
      return `<code>${escapedText}</code>`;
    };

    renderer.hr = function (): string {
      return `<hr role="presentation">`;
    };

    renderer.table = function (header: string, body: string): string {
      return `<div class="ai-chat-table-wrapper"><table>${header}${body}</table></div>`;
    };

    renderer.listitem = function (text: string): string {
      const patterns: { pages: RegExp; lines: RegExp } = {
        pages: /(?<![\w\-\/])p([0-9]+(?:-[0-9]+)?)(?![\w\-\/])/g, // matches p1, p123, p123-456 not within URLs/paths
        lines: /(?<![\w\-\/p])([0-9]+(-[0-9]+))(?![\w\-\/])(?![^<]*[">])/g, // matches 123-456 not within URLs/paths/HTML
      };

      let processedText: string = text;

      for (const [type, pattern] of Object.entries(patterns)) {
        processedText = processedText.replace(pattern, (fullMatch: string, rangeMatch: string) => {
          const [start, end] = rangeMatch.split('-');

          // Determine text based on type and whether it's a range
          const descriptor: string = type === 'pages' && !end ? 'page' : `${type}`;
          const ariaLabel: string = end ? `${descriptor} ${start} to ${end}` : `${descriptor} ${start}`;

          return `<span aria-label="${ariaLabel}">${fullMatch}</span>`;
        });
      }

      const listItemClass: string = tokenClasses[TokenType.LIST_ITEM];
      if (listItemClass) {
        const linkRegex: RegExp = /<a([^>]*)>/g;
        if (linkRegex.test(text)) {
          const styledText: string = text.replace(/<a([^>]*)>/g, `<a$1 class="${listItemClass}">`);
          return `<li>${styledText}</li>`;
        }
      }

      return `<li>${processedText}</li>`;
    };

    renderer.link = function (href: string, title: string | null, text: string): string {
      const classAttr: string = tokenClasses[TokenType.LINK] ? ` class="${tokenClasses[TokenType.LINK]}"` : '';
      const ariaLabelAttr: string = title ? ` aria-label="${title}"` : '';
      const targetAttr: string = href?.startsWith('/') ? '' : ' target="_blank"';
      return `<a href="${href}"${targetAttr}${classAttr}${ariaLabelAttr}>${text}</a>`;
    };

    return {
      gfm: true,
      breaks: true,
      renderer: renderer,
    };
  }

  private static buildMarkedOptionsForReportingAgent(): MarkedOptions {
    const renderer: Renderer = new Renderer();

    renderer.table = function (header: string, body: string): string {
      return `<div class="table table-responsive"><table>${header}${body}</table></div>`;
    };

    return {
      breaks: true,
      renderer: renderer,
    };
  }

  private static buildDefaultMarkedOptions(): MarkedOptions {
    const renderer: Renderer = new Renderer();

    renderer.code = function (text: string): string {
      return `<pre><code>${text}</code></pre>`;
    };

    renderer.codespan = function (text: string): string {
      return `<code>${SharedTextUtility.unescapeAmpersands(text)}</code>`;
    };

    return {
      breaks: true,
      renderer: renderer,
    };
  }

  /**
   * Preprocesses markdown content to protect blank lines in fenced code blocks
   * from being misinterpreted by the marked parser.
   */
  private static preprocessCodeBlocks(value: string): string {
    return value.replace(
      new RegExp(`(${this.FENCE_OPEN_SOURCE})([\\s\\S]*?)(${this.FENCE_CLOSE_SOURCE})`, 'g'),
      (match: string, startFence: string, content: string, endFence: string) => {
        const protectedContent: string = content.replace(/\n\s*\n/g, `\n${this.BLANK_LINE_PLACEHOLDER}\n`);
        return startFence + protectedContent + endFence;
      },
    );
  }

  /**
   * Percent-encodes `|` inside markdown link destinations, leaving fenced code blocks untouched.
   * GFM tables split cells on every unescaped `|`, even inside a link URL, and platform links carry
   * `linkedPropertyData={digitalPropertyId}|{workspaceId}`, so the pipe must be encoded before parsing.
   */
  private static encodePipesInLinkDestinations(value: string): string {
    return value
      .split(new RegExp(`(${this.FENCE_OPEN_SOURCE}[\\s\\S]*?${this.FENCE_CLOSE_SOURCE})`, 'g'))
      .map((segment: string, index: number) =>
        // Odd indexes are the captured fenced code blocks
        index % 2 === 1
          ? segment
          : segment.replace(/\]\(([^)\s]*)/g, (match: string, destination: string) => `](${destination.replace(/\|/g, '%7C')}`),
      )
      .join('');
  }

  public static render(value: string, type: RenderType = RenderType.DEFAULT): string {
    let processedValue: string = value;

    if (type === RenderType.AICHAT) {
      processedValue = this.preprocessCodeBlocks(this.encodePipesInLinkDestinations(value));
    }

    const escapedValue: string = SharedTextUtility.escapeHtmlTags(processedValue);
    const compiledHtml: string = marked(escapedValue, this.getMarkedOptions(type));
    const trustedHtml: TrustedHTML = DOMPurify.sanitize(compiledHtml, RenderTypeSanitizeConfig[type]);
    return trustedHtml.toString();
  }

  /**
   * Renders markdown tables with alternating row background colors and customizable header styling.
   *
   * Defaults are sourced from `shared/constants/custom-report-theme.constants.ts#TableColors`
   * so the rendered output stays in sync with other report surfaces (DOCX export, etc.).
   *
   * Row color logic: the header is rendered separately and does not trigger `tablerow`.
   * The first data row (after the header) receives the `oddColor` and the second data row receives the `evenColor`, alternating thereafter.
   *
   * @param markdown The markdown string containing table(s) to render.
   * @param options Configuration for colors; any field may be overridden.
   * @returns The rendered HTML with styled table rows and header.
   */
  public static renderTable(
    markdown: string,
    {
      evenColor = TableColors.evenRowBg,
      oddColor = TableColors.oddRowBg,
      headerBgColor = TableColors.headerBg,
      headerTextColor = TableColors.headerText,
      borderColor = TableColors.border,
    }: {
      evenColor?: string;
      oddColor?: string;
      headerBgColor?: string;
      headerTextColor?: string;
      borderColor?: string;
    } = {},
  ): string {
    const renderer: Renderer = new Renderer();
    let rowIndex = 0;

    renderer.tablerow = (content: string): string => {
      const bg = rowIndex++ % 2 === 0 ? evenColor : oddColor;
      return `<tr style="background-color: ${bg};">${content}</tr>`;
    };

    renderer.table = (header: string, body: string): string => {
      rowIndex = 0;
      return `<table style="border-collapse: collapse; border: 1px solid ${borderColor};">${header}${body}</table>`;
    };

    renderer.tablecell = (content: string, flags: { header: boolean; align: 'center' | 'left' | 'right' | null }): string => {
      const cellBorderColor = flags.header ? headerBgColor : borderColor;
      const styleParts: string[] = ['padding: 8px', `border: 1px solid ${cellBorderColor}`];

      if (flags.header) {
        styleParts.push(`background-color: ${headerBgColor}`);
        if (headerTextColor) {
          styleParts.push(`color: ${headerTextColor}`);
        }
      }

      const style = ` style="${styleParts.join('; ')};"`;
      const tag = flags.header ? 'th' : 'td';
      const align = flags.align ? ` align="${flags.align}"` : '';

      return `<${tag}${style}${align}>${content}</${tag}>`;
    };

    const compiledHtml: string = marked(markdown, { renderer });
    return DOMPurify.sanitize(compiledHtml).toString();
  }

  public static sanitize(value: string, options: Record<string, any> = {}): string {
    const workingValue: string = SharedTextUtility.escapeHtmlTags(value);
    return DOMPurify.sanitize(workingValue, {
      USE_PROFILES: { html: false },
      ...options,
    });
  }

  /**
   * Converts html content to markdown.
   *
   * @param html html content to convert to markdown.
   * @param options options for the conversion.
   * @param {boolean} [options.keepCodeTagsContent=true] Whether to preserve the content inside code tags. If false, the content inside code tags will also be converted to its html representation.
   *
   * @returns The markdown representation of the provided html.
   */
  public static htmlToMarkdown(html: string, options: { keepCodeTagsContent: boolean } = { keepCodeTagsContent: true }): string {
    if (options.keepCodeTagsContent) {
      const regex: RegExp = new RegExp(/<code>(.*?)<\/code>/g);
      const escapedHtml: string = html.replace(regex, (_: string, innerContent: string): string => {
        return `<code>${SharedTextUtility.escapeHtmlTags(innerContent)}</code>`;
      });

      return this.turndownService.turndown(escapedHtml);
    }
    return this.turndownService.turndown(html);
  }

  /**
   * Finds the highest heading level present in a markdown string.
   * @returns A number between 1 and 6, 1 representing the highest heading which is a h1.
   */
  public static findHighestHeadingLevel(markdownContent: string): number {
    const headingRegex: RegExp = new RegExp(/^(#{1,6})\s+/gm);
    let highestLevel: number = 7; // Higher than the maximum heading level (6)

    let match: RegExpExecArray | null;
    while ((match = headingRegex.exec(markdownContent)) !== null) {
      const level: number = match[1].length; // The number of `#` characters indicates the heading level
      if (level < highestLevel) {
        highestLevel = level;
      }
    }

    // If no heading is found, return null
    return highestLevel === 7 ? null : highestLevel;
  }

  /**
   * Converts JSON to markdown using json2md converters.
   * Input must use json2md converter keys (e.g., 'table', 'h1', 'p', 'ul').
   * @see https://github.com/IonicaBizau/json2md#available-converters
   */
  public static jsonToMarkdown(json: Json2MdInput): string {
    return json2md(json);
  }
}
